import React, { useState, useEffect } from 'react';
import { Plus, Shield, Users, CheckCircle, XCircle, UserCog, Lock, Eye, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import api from '../../api';

const PERMISSIONS = [
  { key: 'manage_schools', label: 'Manage Schools', master: true, school: false, teacher: false, parent: false },
  { key: 'manage_plans', label: 'Manage Plans & Pricing', master: true, school: false, teacher: false, parent: false },
  { key: 'view_revenue', label: 'View Revenue Reports', master: true, school: false, teacher: false, parent: false },
  { key: 'broadcast', label: 'Platform Broadcast', master: true, school: false, teacher: false, parent: false },
  { key: 'manage_users', label: 'Manage Own School Users', master: true, school: true, teacher: false, parent: false },
  { key: 'manage_academic', label: 'Academic Configuration', master: true, school: true, teacher: false, parent: false },
  { key: 'enter_results', label: 'Enter Exam Results', master: true, school: true, teacher: true, parent: false },
  { key: 'view_results', label: 'View Results & Reports', master: true, school: true, teacher: true, parent: true },
  { key: 'lesson_notes', label: 'Create Lesson Notes', master: true, school: false, teacher: true, parent: false },
  { key: 'view_notes', label: 'View Lesson Notes', master: true, school: true, teacher: true, parent: true },
  { key: 'attendance', label: 'Mark Attendance', master: true, school: true, teacher: true, parent: false },
  { key: 'manage_fees', label: 'Manage School Fees', master: true, school: true, teacher: false, parent: false },
];

export default function RolesPage() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => { loadAdmins(); }, []);

  const loadAdmins = async () => {
    setLoading(true);
    try {
      const res = await api.get('/master/users');
      setAdmins(res.data || []);
    } catch { toast.error('Failed to load admins'); }
    finally { setLoading(false); }
  };

  const handleToggle = async (user) => {
    const action = user.is_active ? 'deactivate' : 'activate';
    try {
      await api.post(`/master/users/${user.id}/${action}`);
      toast.success(`Admin ${action}d`);
      loadAdmins();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Action failed'); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-8 animate-fade-in" data-testid="roles-page">
      <div>
        <h1 className="font-outfit text-2xl font-bold text-slate-900">Roles & Access</h1>
        <p className="text-slate-500 text-sm mt-0.5">Manage master admin accounts and view platform role permissions</p>
      </div>

      {/* Master Admin Accounts */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UserCog className="w-4 h-4 text-[#516EFA]" />
            <h3 className="font-outfit font-semibold text-slate-900">Master Admins</h3>
            <Badge className="bg-[#eef1ff] text-[#516EFA] text-xs ml-1">{admins.length}</Badge>
          </div>
          <Button onClick={() => setShowAdd(true)} className="h-8 text-xs bg-[#516EFA] hover:bg-[#4058e8] text-white" data-testid="add-admin-btn">
            <Plus className="w-3.5 h-3.5 mr-1.5" />Add Admin
          </Button>
        </div>

        {loading ? (
          <div className="p-6 space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-14 bg-slate-100 rounded-xl animate-pulse" />)}</div>
        ) : admins.length === 0 ? (
          <div className="py-12 text-center">
            <Users className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            <p className="text-sm text-slate-400">No master admins found</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-50">
            {admins.map((admin, i) => (
              <div key={i} className="px-6 py-4 flex items-center justify-between" data-testid="admin-row">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#516EFA] to-[#14C8C4] flex items-center justify-center text-white font-bold text-sm">
                    {(admin.first_name?.[0] || admin.email?.[0] || 'A').toUpperCase()}
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-slate-800">
                      {admin.first_name && admin.last_name ? `${admin.first_name} ${admin.last_name}` : admin.email}
                    </p>
                    <p className="text-xs text-slate-400">{admin.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className={admin.is_active ? 'badge-success text-xs' : 'badge-neutral text-xs'}>
                    {admin.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                  <p className="text-[11px] text-slate-400 hidden sm:block">
                    {admin.created_at ? new Date(admin.created_at).toLocaleDateString('en-NG') : '—'}
                  </p>
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500 hover:text-red-600" onClick={() => handleToggle(admin)}>
                    {admin.is_active ? <XCircle className="w-3.5 h-3.5" /> : <CheckCircle className="w-3.5 h-3.5" />}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Platform Roles Overview */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
          <Shield className="w-4 h-4 text-[#516EFA]" />
          <h3 className="font-outfit font-semibold text-slate-900">Permission Matrix</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50">
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-64">Permission</th>
                {[
                  { label: 'Master Admin', color: '#516EFA' },
                  { label: 'School Admin', color: '#14C8C4' },
                  { label: 'Teacher', color: '#8B5CF6' },
                  { label: 'Parent/Student', color: '#F59E0B' },
                ].map((role, i) => (
                  <th key={i} className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider" style={{ color: role.color }}>{role.label}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {PERMISSIONS.map((p, i) => (
                <tr key={i} className="hover:bg-slate-50/60 transition-colors">
                  <td className="px-6 py-3 text-sm text-slate-700">{p.label}</td>
                  {[p.master, p.school, p.teacher, p.parent].map((has, j) => (
                    <td key={j} className="px-4 py-3 text-center">
                      {has
                        ? <CheckCircle className="w-4 h-4 text-emerald-500 mx-auto" />
                        : <div className="w-4 h-0.5 bg-slate-200 mx-auto rounded" />
                      }
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Role Descriptions */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { role: 'Master Admin', desc: 'Full platform access. Can manage all schools, billing, plans, and system settings.', icon: Lock, color: '#516EFA' },
          { role: 'School Admin', desc: "Full access to their school's data: teachers, students, academics, fees, results.", icon: Shield, color: '#14C8C4' },
          { role: 'Teacher', desc: "Assigned classes only. Can enter results, write lesson notes, mark attendance.", icon: UserCog, color: '#8B5CF6' },
          { role: 'Parent / Student', desc: "Read-only access to their child's results, lesson notes, fees, and timetable.", icon: Eye, color: '#F59E0B' },
        ].map((r, i) => (
          <div key={i} className="bg-white rounded-2xl p-5 border border-slate-100">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-3" style={{ background: `${r.color}18` }}>
              <r.icon className="w-4 h-4" style={{ color: r.color }} />
            </div>
            <p className="font-semibold text-slate-800 text-sm">{r.role}</p>
            <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">{r.desc}</p>
          </div>
        ))}
      </div>

      {showAdd && <AddAdminModal onClose={() => setShowAdd(false)} onSuccess={() => { setShowAdd(false); loadAdmins(); }} />}
    </div>
  );
}

// ─── Add Admin Modal ───────────────────────────────────────────────────────────

function AddAdminModal({ onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', password: 'Admin@2024' });
  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    if (!form.email) { toast.error('Email is required'); return; }
    setLoading(true);
    try {
      await api.post('/master/users', form);
      toast.success('Admin account created');
      onSuccess();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to create admin'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="font-outfit">Add Master Admin</DialogTitle></DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-sm font-medium mb-1.5 block">First Name</Label><Input value={form.first_name} onChange={e => update('first_name', e.target.value)} placeholder="John" className="h-10" /></div>
            <div><Label className="text-sm font-medium mb-1.5 block">Last Name</Label><Input value={form.last_name} onChange={e => update('last_name', e.target.value)} placeholder="Doe" className="h-10" /></div>
          </div>
          <div><Label className="text-sm font-medium mb-1.5 block">Email</Label><Input value={form.email} onChange={e => update('email', e.target.value)} placeholder="admin@eduva.com" className="h-10" /></div>
          <div><Label className="text-sm font-medium mb-1.5 block">Password</Label><Input value={form.password} onChange={e => update('password', e.target.value)} placeholder="Admin@2024" className="h-10" /></div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button onClick={handleSubmit} disabled={loading} className="flex-1 bg-[#516EFA] text-white">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</> : 'Create Admin'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
